import { useQuery } from "@tanstack/react-query";
import { Award, ChevronRight, Container, DoorOpen } from "lucide-react";
import { Link, useParams } from "react-router-dom";
import { PageHeader } from "@/components/page-header";
import { ProfileInfoCard } from "@/components/profile-info-card";
import {
  Item,
  ItemActions,
  ItemContent,
  ItemDescription,
  ItemMedia,
  ItemTitle,
} from "@/components/ui/item";
import { trpc } from "@/utils/trpc";

function CertificationsSection({ workerId }: { workerId: string }) {
  return (
    <Item asChild variant="outline">
      <Link
        to={`/workers/${workerId}/certifications`}
        aria-label="Manage certifications"
      >
        <ItemMedia>
          <Award size={24} className="text-muted-foreground" />
        </ItemMedia>
        <ItemContent>
          <ItemTitle>Certifications</ItemTitle>
          <ItemDescription>View and upload certifications</ItemDescription>
        </ItemContent>
        <ItemActions>
          <ChevronRight className="size-6" />
        </ItemActions>
      </Link>
    </Item>
  );
}

function ActivitySection({ workerId }: { workerId: string }) {
  return (
    <Item asChild variant="outline">
      <Link
        to={`/workers/${workerId}/sign-ins`}
        aria-label="View worker sign-ins"
      >
        <ItemMedia>
          <DoorOpen size={24} className="text-muted-foreground" />
        </ItemMedia>
        <ItemContent>
          <ItemTitle>Sign-ins</ItemTitle>
          <ItemDescription>Check past and current sign-ins</ItemDescription>
        </ItemContent>
        <ItemActions>
          <ChevronRight className="size-6" />
        </ItemActions>
      </Link>
    </Item>
  );
}

function ApprovalsSection({ workerId }: { workerId: string }) {
  return (
    <Item asChild variant="outline">
      <Link
        to={`/workers/${workerId}/approvals`}
        aria-label="Manage site approvals"
      >
        <ItemMedia>
          <Container size={24} className="text-muted-foreground" />
        </ItemMedia>
        <ItemContent>
          <ItemTitle>Site Approvals</ItemTitle>
          <ItemDescription>
            Check past and current site approvals
          </ItemDescription>
        </ItemContent>
        <ItemActions>
          <ChevronRight className="size-6" />
        </ItemActions>
      </Link>
    </Item>
  );
}

export default function WorkerProfile() {
  const { id } = useParams();
  const workerId = id ?? "";
  const profile = useQuery({
    ...trpc.getWorkerProfile.queryOptions({ id: workerId }),
    enabled: Boolean(id),
  });

  if (profile.isLoading) {
    return <div className="text-muted-foreground">Loading profile…</div>;
  }

  if (profile.error) {
    return <div className="text-red-600">{profile.error.message}</div>;
  }

  if (!profile.data) {
    return <div className="text-muted-foreground">No profile found.</div>;
  }

  const profileData = profile.data;

  return (
    <div className="space-y-6">
      <PageHeader
        title={profileData.fullName}
        description={
          [profileData.company, profileData.jobTitle]
            .filter(Boolean)
            .join(" — ") || undefined
        }
        backTo="/workers"
      />

      {/* Mobile: stacked layout */}
      <div className="space-y-4 lg:hidden">
        <ProfileInfoCard profile={profileData} />
        <div className="space-y-2">
          <ApprovalsSection workerId={workerId} />
          <ActivitySection workerId={workerId} />
          <CertificationsSection workerId={workerId} />
        </div>
      </div>

      {/* Desktop: two-column layout */}
      <div className="hidden lg:grid lg:grid-cols-[320px_1fr] lg:gap-8 lg:items-start">
        <div className="sticky top-24">
          <ProfileInfoCard profile={profileData} />
        </div>
        <div className="space-y-2">
          <ApprovalsSection workerId={workerId} />
          <ActivitySection workerId={workerId} />
          <CertificationsSection workerId={workerId} />
        </div>
      </div>
    </div>
  );
}
